// Utility functions for avatar generation and permission styling

/**
 * Generates a default avatar with initials and color based on the name
 */
export const generateDefaultAvatar = (name: string, size: number = 32) => {
  const initials = name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2)

  // Generate a consistent color based on the name
  const colors = [
    '#FF6B6B', // Red
    '#4ECDC4', // Teal
    '#45B7D1', // Blue
    '#96CEB4', // Green
    '#FFEAA7', // Yellow
    '#DDA0DD', // Plum
    '#FFB347', // Orange
    '#87CEEB', // Sky Blue
    '#F0E68C', // Khaki
    '#D2B48C', // Tan
  ]

  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  const colorIndex = Math.abs(hash) % colors.length
  const backgroundColor = colors[colorIndex]

  // Determine text color based on background brightness
  const hex = backgroundColor.replace('#', '')
  const r = parseInt(hex.substr(0, 2), 16)
  const g = parseInt(hex.substr(2, 2), 16)
  const b = parseInt(hex.substr(4, 2), 16)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000
  const textColor = brightness > 128 ? '#333' : '#fff'

  return {
    initials,
    backgroundColor,
    textColor,
    style: {
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: '50%',
      backgroundColor,
      color: textColor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: `${size * 0.4}px`,
      fontWeight: 'bold',
      flexShrink: 0,
    },
  }
}

/**
 * Get permission badge tone based on permission type
 */
export const getPermissionTone = (
  permission: string
): 'success' | 'info' | 'warning' | 'critical' | 'attention' => {
  const permissionTones: Record<
    string,
    'success' | 'info' | 'warning' | 'critical' | 'attention'
  > = {
    // High-level permissions
    manage_admin: 'critical',

    // Administrative permissions
    view_admin: 'warning',
    login_shops: 'attention',

    // Standard permissions
    view_dashboard: 'success',
    view_shops: 'info',
  }

  return permissionTones[permission] || 'info'
}

/**
 * Permission labels mapping
 */
export const permissionLabels: Record<string, string> = {
  view_dashboard: 'View Dashboard',
  view_shops: 'View Shops',
  login_shops: 'Login to Shops',
  view_admin: 'View Admin Accounts',
  manage_admin: 'Manage Admin Accounts',
}
